import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { assign, isArray, isEmpty } from 'lodash';
import { parseISO, format } from 'date-fns';
import PropTypes from 'prop-types';
import qs from 'querystring';
import * as Yup from 'yup';

import { Spinner, Table } from '~/components';
import { Input, Autocomplete } from '~/components/Form';
import { t } from '~/helpers';
import { storeData, updateData } from '~/store/modules/msgtcaPosto/actions';
import api from '~/services/api';

import * as S from './styles';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();
  const { loading, find, params } = useSelector((state) => state.msgtcaPosto);

  const header = useMemo(
    () => [
      { id: 'post_desc', label: 'Posto' },
      {
        id: 'vemh_datahora',
        label: 'Data',
        format: (date) => format(parseISO(date), 'dd/MM/yyyy HH:mm'),
      },
      { id: 'bico_cod_bico', label: 'Bico' },
      { id: 'oper_cod_oper', label: 'Operador' },
    ],
    []
  );

  const ruleDisabled = useMemo(
    () => Boolean(!create && !isEmpty(find) && Number(find.vems_status) === 2),
    [find, create]
  );

  const [histories, setHistories] = useState([]);
  const [load, setLoad] = useState(false);

  const initials = create ? data : assign(find, data);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const regex = /^[A-Z0-9\(\)\[\]\/\:\?\!\@\$\%\,\-\.\+]+$/;
        const regexValidate =
          'Mensagem informada deve conter apenas letras maíusculas.';

        const schema = Yup.object().shape({
          post_cod_posto_env: Yup.string()
            .typeError('Campo Obrigatório')
            .required('Campo Obrigatório'),
          vems_nome_solic: Yup.string()
            .typeError('Campo Obrigatório')
            .required('Campo Obrigatório'),
          vems_msg01: Yup.string()
            .max(20, 'Quantidade máxima de caracteres é igual a 20.')
            .matches(regex, regexValidate),
          vems_msg02: Yup.string()
            .max(20, 'Quantidade máxima de caracteres é igual a 20.')
            .matches(regex, regexValidate),
          vems_msg03: Yup.string()
            .max(20, 'Quantidade máxima de caracteres é igual a 20.')
            .matches(regex, regexValidate),
          vems_msg04: Yup.string()
            .max(20, 'Quantidade máxima de caracteres é igual a 20.')
            .matches(regex, regexValidate),
        });

        await schema.validate(values, { abortEarly: false });

        const objSend = {
          vems_cod_vems: 0,
          empe_cod_emp: 0,
          veic_cod_veic: 'MSGTCA',
          vems_tamanho_letra: 'P',
          ...initials,
          ...values,
        };

        if (create) dispatch(storeData(objSend, params));
        else dispatch(updateData(objSend, params));
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          reference.current.setErrors(validationErrors);
        }
      }
    },
    [create, params, initials]
  );

  const handleGetAPIHistory = useCallback(async (id) => {
    try {
      setLoad(true);

      const response = await api.get('msgtca/historico', {
        params: {
          wherefield: ['vems_cod_vems'],
          valuewherefield: [id],
          typewherefield: ['EQUALS'],
          pagesize: 99999,
        },
        paramsSerializer: (p) => qs.stringify(p),
      });

      setHistories(response.data.items);
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    } finally {
      setLoad(false);
    }
  }, []);

  useEffect(() => {
    setHistories([]);

    if (!create) {
      handleGetAPIHistory(find.vems_cod_vems);
    }
  }, [create, find]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <S.Wrapper
          ref={reference}
          initialData={initials}
          onSubmit={handleSubmit}
          closure={ruleDisabled}
        >
          <Autocomplete
            name="post_cod_posto_env"
            label="post_desc"
            url="usuarioposto"
            unique="post_cod_posto"
            title="Posto"
            disabled={!create}
          />

          <Input
            name="vems_nome_solic"
            label="Solicitante Abertura"
            disabled={ruleDisabled}
          />

          {ruleDisabled && (
            <Input
              disabled
              name="vems_nome_info_fecha"
              label="Solicitante Fechamento"
            />
          )}

          <Input name="vems_msg01" label="MSG TCA Linha 1" disabled={!create} />

          <Input name="vems_msg02" label="MSG TCA Linha 2" disabled={!create} />

          <Input name="vems_msg03" label="MSG TCA Linha 3" disabled={!create} />

          <Input name="vems_msg04" label="MSG TCA Linha 4" disabled={!create} />

          <Input
            multiline
            name="vems_obs"
            label="Informação Geral"
            rows={4}
            disabled={ruleDisabled}
          />

          {!create && (
            <Table
              title="Histórico de Confirmações"
              size="small"
              cells={header}
              data={histories}
              loading={load}
            />
          )}
        </S.Wrapper>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
